import React from 'react';
import { Box, Button } from '@mui/material';

type WhiteNoteName = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';

type KeyboardRowConfig = {
  label: string;
  width: string;
  maxWidth: string;
  whiteKeys: number;
  startNote: WhiteNoteName;
  highlightIndex: number;
  keyboardHeight: number;
  labelFontSize: string;
};

const whiteNoteCycle: WhiteNoteName[] = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
const hasBlackKeyAfter = new Set<WhiteNoteName>(['C', 'D', 'F', 'G', 'A']);

const keyboardRows: KeyboardRowConfig[] = [
  {
    label: 'Large keyboard',
    width: '96%',
    maxWidth: '940px',
    whiteKeys: 38,
    startNote: 'A',
    highlightIndex: 16,
    keyboardHeight: 118,
    labelFontSize: 'clamp(10px, 0.95vw, 16px)',
  },
  {
    label: 'Medium keyboard',
    width: '67%',
    maxWidth: '590px',
    whiteKeys: 29,
    startNote: 'C',
    highlightIndex: 14,
    keyboardHeight: 110,
    labelFontSize: 'clamp(11px, 1vw, 17px)',
  },
  {
    label: 'Compact keyboard',
    width: '41%',
    maxWidth: '372px',
    whiteKeys: 15,
    startNote: 'C',
    highlightIndex: 7,
    keyboardHeight: 100,
    labelFontSize: 'clamp(12px, 1.05vw, 18px)',
  },
];

const buildWhiteKeyLabels = (startNote: WhiteNoteName, whiteKeys: number) => {
  const startIndex = whiteNoteCycle.indexOf(startNote);
  return Array.from({ length: whiteKeys }, (_, index) => {
    return whiteNoteCycle[(startIndex + index) % whiteNoteCycle.length];
  });
};

const KeyboardReferenceRow: React.FC<KeyboardRowConfig> = ({
  width,
  maxWidth,
  whiteKeys,
  startNote,
  highlightIndex,
  keyboardHeight,
  labelFontSize,
}) => {
  const labels = buildWhiteKeyLabels(startNote, whiteKeys);
  const keybedInset = {
    xs: '34px 16px 10px',
    md: '38px 18px 12px',
  };

  return (
    <Box
      data-middle-c-keyboard-reference
      sx={{
        width,
        maxWidth,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: `${keyboardHeight}px`,
          padding: { xs: '10px 14px 8px', md: '12px 16px 10px' },
          borderRadius: '6px',
          background: 'linear-gradient(180deg, #1c1d21 0%, #07080b 15%, #0b0c10 54%, #111218 100%)',
          boxShadow: '0 14px 28px rgba(0, 0, 0, 0.36)',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '6px',
            left: '18px',
            right: '18px',
            height: '30%',
            borderRadius: '999px',
            background:
              'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.03) 100%)',
            filter: 'blur(1px)',
            opacity: 0.48,
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '22px',
            background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.22) 100%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: keybedInset,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              gridTemplateColumns: `repeat(${whiteKeys}, minmax(0, 1fr))`,
              alignItems: 'end',
            }}
          >
            {labels.map((label, index) => (
              <Box
                key={`white-${label}-${index}`}
                sx={{
                  position: 'relative',
                  height: '100%',
                  borderLeft: index === 0 ? '1px solid rgba(92, 92, 92, 0.32)' : 'none',
                  borderRight: '1px solid rgba(111, 111, 111, 0.42)',
                  borderBottom: '1px solid rgba(150, 150, 150, 0.18)',
                  background: 'linear-gradient(180deg, #fefefe 0%, #f5f3f3 58%, #ebe9ea 100%)',
                  boxShadow:
                    'inset 0 -10px 14px rgba(204, 204, 204, 0.48), inset 0 1px 0 rgba(255,255,255,0.75)',
                }}
              >
                {index === highlightIndex && (
                  <>
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background:
                          'linear-gradient(180deg, rgba(187, 199, 255, 0.28) 0%, rgba(157, 176, 255, 0.72) 100%)',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: '0 auto 0 0',
                        width: '100%',
                        background: 'rgba(150, 166, 255, 0.32)',
                        mixBlendMode: 'multiply',
                      }}
                    />
                  </>
                )}
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
            }}
          >
            {labels.map((label, index) => {
              if (!hasBlackKeyAfter.has(label) || index >= labels.length - 1) return null;

              return (
                <Box
                  key={`black-${label}-${index}`}
                  sx={{
                    position: 'absolute',
                    top: '-1px',
                    left: `${((index + 1) / whiteKeys) * 100}%`,
                    transform: 'translateX(-50%)',
                    width: `calc((100% / ${whiteKeys}) * 0.6)`,
                    height: '58%',
                    borderRadius: '0 0 4px 4px',
                    background:
                      'linear-gradient(180deg, #040507 0%, #1b1d24 8%, #07080b 56%, #16181e 100%)',
                    boxShadow:
                      '0 8px 12px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -3px 8px rgba(255,255,255,0.04)',
                    zIndex: 3,
                  }}
                />
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: `repeat(${whiteKeys}, minmax(0, 1fr))`,
          color: '#0E1117',
          fontWeight: 700,
          letterSpacing: '0.02em',
          lineHeight: 1,
          fontSize: labelFontSize,
          userSelect: 'none',
        }}
      >
        {labels.map((label, index) => (
          <Box
            key={`label-${label}-${index}`}
            sx={{
              textAlign: 'center',
              color: index === highlightIndex ? '#7B91FF' : '#0E1117',
            }}
          >
            {label}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

type MiddleCReferenceProps = {
  onContinue?: () => void;
};

const MiddleCReference: React.FC<MiddleCReferenceProps> = ({ onContinue }) => {
  const [selectedKeyboardIndex, setSelectedKeyboardIndex] = React.useState(0);
  const selectedKeyboard = keyboardRows[selectedKeyboardIndex];

  return (
    <Box
      data-acoustic-setup-step="middle-c"
      sx={{
        borderRadius: '18px',
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.025) 100%)',
        border: '1px solid rgba(121, 216, 255, 0.18)',
        padding: { xs: '14px', md: '14px 16px' },
      }}
    >
      <Box
        sx={{
          color: '#F5F9FF',
          fontSize: { xs: '20px', md: '22px' },
          fontWeight: 700,
          letterSpacing: 0,
          textAlign: 'center',
          marginBottom: '4px',
        }}
      >
        Find middle C
      </Box>

      <Box
        sx={{
          color: 'rgba(219, 228, 247, 0.76)',
          fontSize: '14px',
          textAlign: 'center',
          marginBottom: '10px',
        }}
      >
        Select the keyboard closest to yours.
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          flexWrap: 'wrap',
          marginBottom: '10px',
        }}
      >
        {keyboardRows.map((row, index) => {
          const selected = selectedKeyboardIndex === index;
          return (
            <Button
              key={row.label}
              data-middle-c-keyboard-size={row.label.toLowerCase().replace(' keyboard', '')}
              aria-pressed={selected}
              variant={selected ? 'contained' : 'outlined'}
              onClick={() => setSelectedKeyboardIndex(index)}
              sx={{
                minHeight: '36px',
                paddingX: '14px',
                borderRadius: '10px',
                borderColor: selected ? '#6F87F1' : '#667086',
                backgroundColor: selected ? '#2A3866' : 'transparent',
                color: '#F2F6FF',
                fontSize: '13px',
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#8298FF',
                  backgroundColor: selected ? '#2A3866' : 'rgba(111, 135, 241, 0.08)',
                },
              }}
            >
              {row.label}
            </Button>
          );
        })}
      </Box>

      <Box
        sx={{
          borderRadius: '16px',
          background: 'linear-gradient(180deg, #e6e6e6 0%, #d9d9d9 100%)',
          minHeight: { xs: '166px', md: '176px' },
          padding: { xs: '12px 10px', md: '14px 14px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <KeyboardReferenceRow
          key={`${selectedKeyboard.startNote}-${selectedKeyboard.whiteKeys}`}
          {...selectedKeyboard}
        />
      </Box>

      {onContinue && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
          <Button
            data-acoustic-setup-continue
            variant="contained"
            onClick={onContinue}
            sx={{
              minHeight: '42px',
              paddingX: '22px',
              borderRadius: '10px',
              backgroundColor: '#586FCB',
              color: '#FFFFFF',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': { backgroundColor: '#4C61B4' },
            }}
          >
            Continue
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default MiddleCReference;
